import { useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { Step } from '@percent/grants-nomination-form/ui';
import { NominationFormPageProps } from './NominationFormPage.types';
import { Checkbox, Flex } from '@percent/lemonade';
import { formatKey } from '@percent/grants-nomination-form/utils/formKey';
import { NominationFormQuestion } from './NominationFormQuestion/NominationFormQuestion';
import { NOMINATION_NOMINATOR_ORG_CONSENT_KEY, useShareNominatorOrgConsent } from './hooks/useShareNominatorWithOrg';

export function NominationFormPage({
  page,
  onNext,
  onBack,
  isLastPage,
  isLoading,
  progress,
  errorMessage,
  shareNominatorInfoWithOrg,
  allowedOrganisationsTypes,
}: NominationFormPageProps) {
  const { t } = useTranslation();
  const { formState } = useFormContext();
  const { shareNominatorOrgConsent, toggleShareNominatorOrgConsent } =
    useShareNominatorOrgConsent(shareNominatorInfoWithOrg);

  const hasCurrentPageErrors = page.questions?.some((question) => !!get(formState.errors, formatKey(question.key)));
  const countryCodeQuestionKey = page.questions?.find((question) => question.type === 'country_search')?.key;
  const organisationQuestionKey = page.questions?.find((question) => question.type === 'organisation_search')?.key;

  const isShareConsentRequired = isLastPage && shareNominatorInfoWithOrg && !shareNominatorOrgConsent;

  const nextButtonLabel = isLastPage ? t('button.submit') : t('button.next');

  return (
    <Step
      title={page.title}
      description={page.description}
      onNext={onNext}
      onBack={onBack}
      nextButtonDisabled={hasCurrentPageErrors || isShareConsentRequired}
      nextButtonLabel={nextButtonLabel}
      isLoading={isLoading}
      progress={progress}
      errorMessage={errorMessage}
      withTermsCheckbox={isLastPage}
    >
      <Flex direction="column" gap={16} mb={24} w="100%">
        {page.questions
          ?.sort((a, b) => a.order - b.order)
          .map((question) => (
            <NominationFormQuestion
              key={question.id}
              question={question}
              countryCodeQuestionKey={countryCodeQuestionKey}
              organisationQuestionKey={organisationQuestionKey}
              allowedOrganisationsTypes={allowedOrganisationsTypes}
            />
          ))}
      </Flex>

      {isLastPage && shareNominatorInfoWithOrg && (
        <Flex direction="column" w="100%">
          <Checkbox
            name={NOMINATION_NOMINATOR_ORG_CONSENT_KEY}
            label={t('typography.nominatorInfoCheckbox')}
            active={shareNominatorOrgConsent}
            onChange={toggleShareNominatorOrgConsent}
            variant="default"
          />
        </Flex>
      )}
    </Step>
  );
}
